/**
 * The connection form's label-left layout (Option C, C2, plan §3.4). The container is the form content box inside the
 * pane padding (`@container/connection-form` on the `<form>`); a row puts its label in a 132-px column left of the
 * control once that box is at least 31 rem (496 px = `LABEL_LEFT_MIN_CONTENT`, the same boundary `dialogGeometry`
 * computes), and above the control otherwise — the app's standard label-above form. Pure CSS: no measuring, so the
 * label never jumps after the first paint, and it holds in every height tier.
 *
 * Tailwind generates classes from the literal strings in source, so the boundary is spelled out in each class below;
 * `formLayout.test.ts` pins every `@min-[…]` to `LABEL_LEFT_MIN_CONTENT / 16` rem.
 *
 * `Docs/UI.md` records the exception: label-left exists only in this dialog, and only through this container.
 */
export { LABEL_LEFT_MIN_CONTENT } from '../hooks/dialogGeometry';

/** The `<form>`: the container every row measures. */
export const FORM_CONTAINER_CLASS = '@container/connection-form';

/** One row: label above (default), or a 132-px label column + 16-px gap + the control from 31 rem. */
export const ROW_CLASS = 'grid grid-cols-1 gap-y-1 @min-[31rem]/connection-form:grid-cols-[132px_minmax(0,1fr)] @min-[31rem]/connection-form:gap-x-4';

/** The label cell: wraps inside its column; right-aligned and centred on a 36-px control when left of it. */
export const LABEL_CELL_CLASS = 'min-w-0 break-words text-sm font-medium leading-5 @min-[31rem]/connection-form:pt-2 @min-[31rem]/connection-form:text-right';

/** "optional": after the label when above, a second muted line when left. */
export const LABEL_NOTE_CLASS = 'ml-1 text-xs font-normal text-muted-foreground @min-[31rem]/connection-form:ml-0 @min-[31rem]/connection-form:block';

/** A row without a label (a lone checkbox): the empty label column keeps the control aligned with the others. */
export const EMPTY_LABEL_CELL_CLASS = 'hidden @min-[31rem]/connection-form:block';

/** Every `@min-[…]` boundary used above, for the pin test. */
export const LABEL_LEFT_CLASSES = [ROW_CLASS, LABEL_CELL_CLASS, LABEL_NOTE_CLASS, EMPTY_LABEL_CELL_CLASS];
